import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { colors, fontSize } from '../../../global/variables';
import { buildExcludePayload } from '../../../utils/avoidAreaHelpers';

const { secondaryTextColor, primaryTextColor, primaryBorderColor } = colors;
const { small, smaller } = fontSize;

const StyledSection = styled.details({
  backgroundColor: 'rgba(255,102,102,.02)',
  border: '1px solid rgba(255,102,102,.3)',
  borderRadius: '0.12rem',
  padding: '0.75rem',
  marginBottom: '0.9rem',
});

const StyledSummary = styled.summary({
  fontSize: small,
  fontWeight: 700,
  color: 'rgba(180,50,50,.85)',
  cursor: 'pointer',
});

const StyledSummaryHint = styled.span({
  fontSize: smaller,
  fontWeight: 400,
  color: secondaryTextColor,
  marginLeft: '0.3rem',
});

const StyledSubTitle = styled.div({
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: secondaryTextColor,
  marginTop: '0.75rem',
  marginBottom: '0.4rem',
});

const StyledInputRow = styled.div({
  display: 'flex',
  gap: '0.3rem',
  marginBottom: '0.3rem',
});

const StyledTextInput = styled.input({
  flex: 1,
  fontSize: smaller,
  padding: '0.25rem 0.45rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  fontFamily: 'monospace',
});

const StyledAddButton = styled.button({
  flexShrink: 0,
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.22rem 0.55rem',
  fontSize: smaller,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  ':hover': { borderColor: 'rgba(200,60,60,.9)', color: 'rgba(200,60,60,.9)' },
});

const StyledExcludeTag = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.18rem 0.5rem 0.18rem 0.38rem',
  borderRadius: '1.5rem',
  fontSize: smaller,
  fontWeight: 500,
  margin: '0.15rem 0.3rem 0.15rem 0',
  backgroundColor: 'rgba(255,102,102,.1)',
  border: '1px solid rgba(255,102,102,.35)',
  color: 'rgba(200,60,60,.9)',
});

const StyledExcludeTagRemove = styled.button({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.6,
  fontSize: smaller,
  padding: 0,
  lineHeight: 1,
  ':hover': { opacity: 1 },
});

const StyledEmptyLabel = styled.div({
  fontSize: smaller,
  color: primaryTextColor,
  fontStyle: 'italic',
  marginBottom: '0.4rem',
});

const StyledNoPreviewNote = styled.div({
  fontSize: smaller,
  color: secondaryTextColor,
  fontStyle: 'italic',
  marginBottom: '0.4rem',
});

const AvoidExcludeEditor = ({
  exclude,
  onChange,
  labels: {
    excludeTitle,
    excludeHint,
    excludeCountriesTitle,
    excludeCountryPlaceholder,
    excludeAddLabel,
    excludeCountryRemoveTooltip,
    excludeCountriesEmptyLabel,
    excludeNoPreviewNote,
  },
}) => {
  const [countries, setCountries] = useState(exclude?.countries || []);
  const [countryInput, setCountryInput] = useState('');

  useEffect(() => {
    onChange(buildExcludePayload({ countries }));
  }, [countries, onChange]);

  const onAddCountry = useCallback(() => {
    const value = countryInput.trim().toUpperCase();
    if (!value) return;
    setCountries((prev) => [...prev, value]);
    setCountryInput('');
  }, [countryInput]);

  const onRemoveCountry = useCallback(
    (index) => setCountries((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  return (
    <StyledSection>
      <StyledSummary>
        {excludeTitle}
        <StyledSummaryHint>{excludeHint}</StyledSummaryHint>
      </StyledSummary>
      <StyledNoPreviewNote>{excludeNoPreviewNote}</StyledNoPreviewNote>
      <StyledSubTitle>{excludeCountriesTitle}</StyledSubTitle>
      <StyledInputRow>
        <StyledTextInput
          type="text"
          placeholder={excludeCountryPlaceholder}
          value={countryInput}
          onChange={(e) => setCountryInput(e.target.value)}
        />
        <StyledAddButton
          type="button"
          id="button-fleet-profile-exclude-country-add"
          onClick={onAddCountry}
        >
          {excludeAddLabel}
        </StyledAddButton>
      </StyledInputRow>
      {countries.length === 0 && <StyledEmptyLabel>{excludeCountriesEmptyLabel}</StyledEmptyLabel>}
      {countries.map((country, index) => (
        <StyledExcludeTag key={`${country}-${index}`}>
          {'\uD83D\uDEAB '}
          {country}
          <StyledExcludeTagRemove
            type="button"
            id={`button-fleet-profile-exclude-country-remove-${index}`}
            title={excludeCountryRemoveTooltip}
            onClick={() => onRemoveCountry(index)}
          >
            ×
          </StyledExcludeTagRemove>
        </StyledExcludeTag>
      ))}
    </StyledSection>
  );
};

export default AvoidExcludeEditor;
